/**
 * Analytics Event Types and Queue Types
 */

// Navigation events
type NavigationEvent = 'screen_view' | 'tab_switch';

// Auth events
type AuthEvent = 'sign_up' | 'sign_in' | 'sign_out';

// Startup events
type StartupEvent =
  | 'profile_created'
  | 'profile_updated'
  | 'score_viewed'
  | 'score_refreshed'
  | 'data_room_connected'
  | 'document_uploaded'
  | 'pitch_deck_analyzed'
  | 'fundraising_round_closed';

// Investor events
type InvestorEvent =
  | 'dealflow_viewed'
  | 'dealflow_status_changed'
  // Due diligence entry — starting DD is the explicit data-room access request
  | 'dd_started'
  | 'dd_opened_from_dealflow'
  | 'startup_discovered'
  | 'startup_detail_viewed'
  | 'data_room_accessed'
  | 'document_viewed'
  | 'document_downloaded'
  // Submissions inbox
  | 'submissions_inbox_viewed'
  | 'submission_shortlisted'
  | 'submission_rejected'
  | 'submission_archived'
  | 'submission_info_requested'
  | 'submission_claim_invited'
  | 'submission_bulk_shortlist'
  | 'submission_bulk_reject'
  | 'submission_bulk_archive'
  | 'submission_link_copied'
  | 'submission_link_shared'
  | 'submission_link_setup_navigated'
  | 'submission_detail_viewed';

// Messaging events
type MessagingEvent = 'conversation_opened' | 'message_sent';

// DD events
type DDEvent =
  | 'dd_chat_opened'
  | 'dd_chat_message_sent'
  | 'dd_rag_retrieval'
  | 'shared_dd_created'
  | 'shared_dd_invite_sent';

// Commitment events
type CommitmentEvent =
  | 'commitment_created'
  | 'commitment_updated'
  | 'commitment_withdrawn'
  | 'commitment_converted';

// Per-persona onboarding activation funnel events (proposal §8). Each fires
// ONLY after the step's awaited DB write succeeds — never on screen view/mount.
// Grouped by persona in `activation-events.ts` (the single source of truth for
// which events belong to which funnel).
type ActivationEvent =
  // Founder: signup_confirmed → founder_personal_done → company_resolved →
  // essentials_done → data_source_chosen → first_score
  | 'founder_personal_done'
  | 'company_resolved'
  | 'essentials_done'
  | 'data_source_chosen'
  | 'first_score'
  // Invited member: invite_viewed → invite_accepted → member_personal_done →
  // entered_workspace
  | 'invite_viewed'
  | 'invite_accepted'
  | 'member_personal_done'
  | 'entered_workspace'
  // Investor: investor_identity_done → thesis_done → dealflow_viewed
  // (`dealflow_viewed` already exists under InvestorEvent — reused, not redefined)
  | 'investor_identity_done'
  | 'thesis_done';

// Other events
type OtherEvent =
  | 'feedback_submitted'
  | 'pricing_viewed'
  | 'subscription_started'
  | 'subscription_canceled'
  | 'onboarding_step_completed'
  | 'onboarding_completed'
  | 'onboarding_company_essentials_completed'
  | 'onboarding_company_join_requested'
  | 'onboarding_data_choice'
  | 'onboarding_essentials_skipped'
  | 'settings_opened'
  | 'mfa_enabled'
  | 'mfa_disabled'
  | 'waitlist_signup'
  | 'data_exported'
  | 'account_deleted';

export type AnalyticsEventName =
  | NavigationEvent
  | AuthEvent
  | StartupEvent
  | InvestorEvent
  | MessagingEvent
  | DDEvent
  | CommitmentEvent
  | ActivationEvent
  | OtherEvent;

export type EventCategory =
  | 'navigation'
  | 'auth'
  | 'interaction'
  | 'conversion'
  | 'engagement'
  | 'system';

/** Maps event names to their categories */
export const EVENT_CATEGORIES: Record<AnalyticsEventName, EventCategory> = {
  // Navigation
  screen_view: 'navigation',
  tab_switch: 'navigation',
  // Auth
  sign_up: 'auth',
  sign_in: 'auth',
  sign_out: 'auth',
  // Startup
  profile_created: 'interaction',
  profile_updated: 'interaction',
  score_viewed: 'engagement',
  score_refreshed: 'engagement',
  data_room_connected: 'conversion',
  document_uploaded: 'interaction',
  pitch_deck_analyzed: 'engagement',
  fundraising_round_closed: 'conversion',
  // Investor
  dealflow_viewed: 'engagement',
  dealflow_status_changed: 'interaction',
  dd_started: 'conversion',
  dd_opened_from_dealflow: 'engagement',
  startup_discovered: 'engagement',
  startup_detail_viewed: 'engagement',
  data_room_accessed: 'engagement',
  document_viewed: 'engagement',
  document_downloaded: 'interaction',
  // Submissions inbox
  submissions_inbox_viewed: 'engagement',
  submission_shortlisted: 'interaction',
  submission_rejected: 'interaction',
  submission_archived: 'interaction',
  submission_info_requested: 'interaction',
  submission_claim_invited: 'conversion',
  submission_bulk_shortlist: 'interaction',
  submission_bulk_reject: 'interaction',
  submission_bulk_archive: 'interaction',
  submission_link_copied: 'interaction',
  submission_link_shared: 'interaction',
  submission_link_setup_navigated: 'navigation',
  submission_detail_viewed: 'engagement',
  // Messaging
  conversation_opened: 'engagement',
  message_sent: 'interaction',
  // DD
  dd_chat_opened: 'engagement',
  dd_chat_message_sent: 'interaction',
  dd_rag_retrieval: 'engagement',
  shared_dd_created: 'conversion',
  shared_dd_invite_sent: 'conversion',
  // Commitments
  commitment_created: 'conversion',
  commitment_updated: 'interaction',
  commitment_withdrawn: 'interaction',
  commitment_converted: 'conversion',
  // Onboarding activation funnel (per-persona)
  founder_personal_done: 'conversion',
  company_resolved: 'conversion',
  essentials_done: 'conversion',
  data_source_chosen: 'conversion',
  first_score: 'conversion',
  invite_viewed: 'engagement',
  invite_accepted: 'conversion',
  member_personal_done: 'conversion',
  entered_workspace: 'conversion',
  investor_identity_done: 'conversion',
  thesis_done: 'conversion',
  // Other
  feedback_submitted: 'interaction',
  pricing_viewed: 'conversion',
  subscription_started: 'conversion',
  subscription_canceled: 'conversion',
  onboarding_step_completed: 'interaction',
  onboarding_completed: 'conversion',
  onboarding_company_essentials_completed: 'interaction',
  onboarding_company_join_requested: 'interaction',
  onboarding_data_choice: 'interaction',
  onboarding_essentials_skipped: 'interaction',
  settings_opened: 'navigation',
  mfa_enabled: 'interaction',
  mfa_disabled: 'interaction',
  waitlist_signup: 'conversion',
  data_exported: 'interaction',
  account_deleted: 'conversion',
};

/** A queued event waiting to be flushed to Supabase */
export interface QueuedEvent {
  user_id: string;
  session_id: string | null;
  event_name: AnalyticsEventName;
  event_category: EventCategory;
  properties: Record<string, unknown>;
  platform: string;
  app_version: string;
  device_info: Record<string, unknown>;
  created_at: string;
}
